// Complete implementation of feedback functionality in ChatbotWidget.js

import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { BsChatDots, BsX } from "react-icons/bs";
import { HiSparkles } from "react-icons/hi";
import { AiOutlineSend, AiOutlineLike, AiOutlineDislike } from "react-icons/ai";
import { FiCopy, FiUser } from "react-icons/fi";
import { IoIosOptions } from "react-icons/io";
import Dropdown from "components/dropdown";
import Request from "helpers/request";
import { urls } from "helpers/urls";
import CommonNotification from "./common/CommonNotification";
import { Spin } from "antd";
import { IoDocumentTextOutline } from "react-icons/io5";

// Import typing cursor styles
import "../assets/css/typing-cursor.css";

// Stepped status loader shown while we wait for the bot's response.
// Cycles through the steps and holds on the last one until the answer arrives.
const LOADER_STEPS = [
  "Understanding your question",
  "Searching the knowledge base",
  "Generating your answer",
];

const ChatbotStepLoader = ({ textColor = "#fff" }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
    const timers = [
      setTimeout(() => setStep(1), 1100),
      setTimeout(() => setStep(2), 2300),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div
      className="op_chatbot_step_loader flex flex-col gap-[10px] py-[2px]"
      style={{ color: textColor }}
    >
      {LOADER_STEPS.map((label, idx) => {
        const isActive = idx === step;
        const isDone = idx < step;
        const isPending = idx > step;
        return (
          <div
            key={label}
            className="flex items-center gap-[10px] text-[15px] font-medium leading-snug transition-opacity duration-300"
            style={{ opacity: isPending ? 0.5 : 1 }}
          >
            <span
              className="inline-flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center"
              aria-hidden="true"
            >
              {isDone ? (
                <svg viewBox="0 0 16 16" className="h-[18px] w-[18px]" fill="none">
                  <path
                    d="M3 8.5l3.2 3.2L13 5"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : isActive ? (
                <span
                  className="op_chatbot_step_spinner inline-block h-[16px] w-[16px] rounded-full"
                  style={{
                    border: "2.5px solid currentColor",
                    borderTopColor: "transparent",
                    animation: "op_chatbot_spin 0.7s linear infinite",
                  }}
                />
              ) : (
                <span
                  className="inline-block h-[8px] w-[8px] rounded-full"
                  style={{ background: "currentColor", opacity: 0.4 }}
                />
              )}
            </span>
            <span>
              {label}
              {isActive ? (
                <span className="op_chatbot_step_dots" aria-hidden="true">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              ) : isPending ? (
                "..."
              ) : null}
            </span>
          </div>
        );
      })}
      <style>{`
        @keyframes op_chatbot_spin {
          to { transform: rotate(360deg); }
        }
        @keyframes op_chatbot_step_dot {
          0%, 20% { opacity: 0.2; }
          50% { opacity: 1; }
          100% { opacity: 0.2; }
        }
        .op_chatbot_step_dots span {
          display: inline-block;
          animation: op_chatbot_step_dot 1.2s infinite ease-in-out;
        }
        .op_chatbot_step_dots span:nth-child(2) { animation-delay: 0.2s; }
        .op_chatbot_step_dots span:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};

ChatbotStepLoader.propTypes = {
  textColor: PropTypes.string,
};

const ChatbotWidget = ({
  // ... all your existing props
  widgetPosition,
  headerColor,
  agentMessageColor,
  agentTextColor,
  widgetIconColor,
  widgetIcon = null,
  input = "",
  setInput = () => {},
  isLoading = false,
  setIsLoading = () => {},
  messagesEndRef = null,
  bot_type = "readonly",
  isOpen = false,
  setIsOpen = () => {},
  liked = false,
  setLiked = () => {},
  disliked = false,
  setDisliked = () => {},
  copiedMsg = false,
  setCopiedMsg = () => {},
  messages = [],
  onSendMessage = () => {},
  onCloseMessage = () => {},
  onStartTyping = () => {},
  onFinishTyping = () => {},
  sessionId = null,
  siteId = null,
  isSticky = true,
  setSessionId = () => {},
}) => {
  // ... all your existing state and useEffect hooks

  const localMessagesEndRef = useRef(null);
  const endRef = messagesEndRef || localMessagesEndRef;
  const [messageStates, setMessageStates] = useState({});
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [typingMessage, setTypingMessage] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);

  // Utility function to strip HTML tags
  const stripHtmlTags = (str) => {
    if (!str) return "";
    return str.replace(/<[^>]*>/g, "");
  };

  // Updated handleMessageRating function with API integration
  const handleMessageRating = async (messageIndex, action) => {
    const message = messages[messageIndex];
    const currentState = messageStates[messageIndex] || {
      liked: false,
      disliked: false,
      copied: false,
    };

    // Validate required data
    if (!message?.id || !sessionId || !siteId) {
      console.error("Missing required data for feedback:", {
        messageId: message?.id,
        sessionId,
        siteId,
      });
      CommonNotification.error(
        "Unable to record feedback. Missing required information."
      );
      return;
    }

    // Additional validation: Check if message ID looks like a fallback ID
    if (message.id.includes("agent_") && message.id.includes("_0.")) {
      console.warn(
        "Warning: Message ID appears to be a fallback ID:",
        message.id
      );
      CommonNotification.error(
        "Message ID is invalid. Please refresh the page and try again."
      );
      return;
    }

    // Determine the new feedback value based on current state and action
    let feedbackValue = 0; // 0 = no feedback (neutral)

    if (action === "like") {
      // If already liked, remove like (set to 0), otherwise set to like (1)
      feedbackValue = currentState.liked ? 0 : 1;
    } else if (action === "dislike") {
      // If already disliked, remove dislike (set to 0), otherwise set to dislike (-1)
      feedbackValue = currentState.disliked ? 0 : -1;
    }

    // Update local state immediately for better UX
    const newState = {
      ...currentState,
      liked: feedbackValue === 1,
      disliked: feedbackValue === -1,
    };

    setMessageStates((prev) => ({
      ...prev,
      [messageIndex]: newState,
    }));

    // Prepare payload for API
    const payload = {
      session_id: sessionId,
      feedback: feedbackValue,
      site_id: siteId,
      message_id: message.id,
    };

    // Send feedback to API
    try {
      const response = await Request.postJson(urls.chatFeedback, payload);

      if (response?.data?.success) {
        console.log("Feedback recorded successfully:", response.data.message);
        // Optional: Show success notification (uncomment if desired)
        // CommonNotification.success(`Feedback ${feedbackValue === 1 ? 'like' : feedbackValue === -1 ? 'dislike' : 'removed'} recorded`);
      } else {
        throw new Error("API response indicates failure");
      }
    } catch (error) {
      console.error("Failed to send feedback:", error);
      console.error("Error details:", error.response?.data);

      // Revert the local state on error
      setMessageStates((prev) => ({
        ...prev,
        [messageIndex]: currentState,
      }));

      // Show error notification with more details
      const errorMessage =
        error.response?.data?.message ||
        "Failed to record feedback. Please try again.";
      CommonNotification.error(errorMessage);
    }
  };

  // Handle message copy functionality
  const handleCopyMessage = (messageIndex, text) => {
    const plainText = stripHtmlTags(text);
    navigator.clipboard
      .writeText(plainText)
      .then(() => {
        setMessageStates((prev) => ({
          ...prev,
          [messageIndex]: {
            ...prev[messageIndex],
            copied: true,
          },
        }));

        // Reset copy state after 2 seconds
        setTimeout(() => {
          setMessageStates((prev) => ({
            ...prev,
            [messageIndex]: {
              ...prev[messageIndex],
              copied: false,
            },
          }));
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        CommonNotification.error("Failed to copy message");
      });
  };

  // Handle send button click with typing animation
  const handleSendMessage = async () => {
    if (input.trim() && !isLoading) {
      setInput("");

      // Force scroll to bottom when user sends a message
      setUserScrolling(false);

      // Scroll to bottom immediately before sending message
      if (endRef.current) {
        endRef.current.scrollIntoView({
          behavior: "auto",
          block: "end",
        });
      }

      // Call the parent component's message handler with typing animation
      await onSendMessage(input.trim(), null);
    }
  };

  // Handle Enter key press with typing animation
  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        // Force scroll to bottom when user sends a message
        setUserScrolling(false);

        // Scroll to bottom immediately before sending message
        if (endRef.current) {
          endRef.current.scrollIntoView({
            behavior: "auto",
            block: "end",
          });
        }

        await onSendMessage(input.trim(), null);
      }
    }
  };

  const animateTyping = (fullText, messageId, sources = []) => {
    return new Promise((resolve) => {
      // Clear any existing typing animation
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }

      // Strip HTML tags from the text to prevent them from showing during typing
      const cleanText = stripHtmlTags(fullText);

      setTypingMessage({
        id: messageId,
        text: "",
        type: "agent",
        sources: sources,
        originalHtml: fullText, // Store original HTML for final display
      });

      const paragraphs = cleanText.split(/\n\s*\n|\r\n\s*\r\n/);
      let currentParagraphIndex = 0;
      let currentWordIndex = 0;
      let displayedText = "";

      // Process paragraphs into words
      const paragraphWords = paragraphs.map((paragraph) =>
        paragraph.trim().split(/\s+/)
      );

      // Base typing speed with randomization for natural feel
      const getTypingSpeed = () => {
        // Base speed depends on word length for more natural flow
        const baseSpeed = 70; // Slightly faster speed while maintaining smoothness

        // Add less randomness (±15% variation) for more consistent timing
        return baseSpeed * (0.925 + Math.random() * 0.15);
      };

      const typeNextWord = () => {
        if (currentParagraphIndex < paragraphWords.length) {
          const currentParagraph = paragraphWords[currentParagraphIndex];

          if (currentWordIndex < currentParagraph.length) {
            // Add the next word with a space (except for the first word in a paragraph)
            const nextWord = currentParagraph[currentWordIndex];
            const isFirstWordInParagraph = currentWordIndex === 0;
            const needsSpace = !isFirstWordInParagraph;

            // Add paragraph break if this is the first word of a new paragraph (except the first paragraph)
            if (isFirstWordInParagraph && currentParagraphIndex > 0) {
              displayedText += "\n\n" + nextWord;
            } else {
              displayedText += (needsSpace ? " " : "") + nextWord;
            }

            // Use requestAnimationFrame for smoother updates
            requestAnimationFrame(() => {
              setTypingMessage((prev) => ({
                ...prev,
                text: displayedText,
              }));
            });

            currentWordIndex++;
          } else {
            // Move to next paragraph
            currentParagraphIndex++;
            currentWordIndex = 0;
            typeNextWord(); // Process next paragraph
            return;
          }

          // Schedule next word with variable timing
          let delay = getTypingSpeed();

          // Adjust timing based on word length
          const currentWord = currentParagraph[currentWordIndex - 1]; // Get the word we just added
          const wordLength = currentWord.length;
          if (wordLength > 8) {
            delay *= 1.5; // Longer words
          } else if (wordLength < 3) {
            delay *= 0.8; // Shorter words
          }

          // Add extra delay for punctuation
          if (currentWord.match(/[,.!?;:]$/)) {
            delay *= 2.5; // Pause at punctuation
          }

          // Schedule next word
          const timeout = setTimeout(typeNextWord, delay);
          setTypingTimeout(timeout);
        } else {
          // Typing animation complete
          setTypingMessage(null);
          resolve();
        }
      };

      // Start typing animation
      typeNextWord();
    });
  };

  // Track if user is manually scrolling
  const [userScrolling, setUserScrolling] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const chatContainerRef = useRef(null);

  // Smart scroll on new messages:
  // - If the latest message is from the USER (they just sent), scroll the
  //   chat to the bottom so they can see their message + the step loader.
  // - If the latest message is from the AGENT (response just arrived), anchor
  //   the TOP of that message to the top of the visible area so the user can
  //   start reading from the beginning instead of being yanked to the bottom
  //   of a long reply.
  const prevMessagesRef = useRef({ length: 0, lastType: null });
  useEffect(() => {
    const chatContainer = document.querySelector(".op_chatbot_body_messages");
    if (!chatContainer) return;
    if (!chatContainerRef.current) chatContainerRef.current = chatContainer;

    const prev = prevMessagesRef.current;
    const len = messages.length;
    const last = len ? messages[len - 1] : null;
    const grewByOne = len === prev.length + 1;
    prevMessagesRef.current = { length: len, lastType: last?.type ?? null };

    if (!last) return;

    // Fresh agent reply right after a user message → anchor TOP of the new
    // agent message at the top of the viewport so the user can read from the
    // start instead of being yanked to the bottom of a long answer.
    if (grewByOne && last.type === "agent" && prev.lastType === "user") {
      const lastId = String(last.id ?? len - 1);
      // Defer twice: once for React to commit, once for layout.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = chatContainer.querySelector(
            `[data-msg-id="${CSS.escape(lastId)}"][data-msg-type="agent"]`
          );
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });
      return;
    }

    // Fresh user message → keep them pinned to the bottom so they see what
    // they just sent + the step loader appearing underneath.
    if (grewByOne && last.type === "user") {
      requestAnimationFrame(() => {
        if (endRef.current) {
          endRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        } else {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    }
    // Bulk history load (length jumps by >1) → the mount-only load effect
    // scrolls to bottom; nothing to do here.
  }, [messages, endRef]);

  // When the step loader appears, make sure it's visible at the bottom
  // (covers the case where the loader mounts after the user-message scroll).
  useEffect(() => {
    if (!isLoading) return;
    const chatContainer = document.querySelector(".op_chatbot_body_messages");
    requestAnimationFrame(() => {
      if (endRef.current) {
        endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      } else if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    });
  }, [isLoading, endRef]);

  // Ensure chat scrolls to bottom on page load/refresh
  useEffect(() => {
    // Scroll to bottom on initial load
    const scrollToBottomOnLoad = () => {
      if (endRef.current) {
        // Use a small delay to ensure DOM is fully rendered
        setTimeout(() => {
          endRef.current.scrollIntoView({
            behavior: "auto",
            block: "end",
          });

          // Force scroll container to bottom
          const chatContainer = document.querySelector(
            ".op_chatbot_body_messages"
          );
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
      }
    };

    // Call immediately and also after a short delay to ensure it works
    scrollToBottomOnLoad();

    // Try multiple times with increasing delays to ensure it works
    // even when the DOM takes longer to render or when images load
    const timeoutIds = [
      setTimeout(scrollToBottomOnLoad, 500),
      setTimeout(scrollToBottomOnLoad, 1000),
      setTimeout(scrollToBottomOnLoad, 2000),
    ];

    return () => timeoutIds.forEach((id) => clearTimeout(id));
    // Mount only — incremental message scroll is handled by the smart effect
    // above. Re-running this on every messages.length change would force the
    // chat to the bottom and fight the agent-message top-anchor behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endRef]);

  // Separate effect for scroll event listener with proper cleanup
  useEffect(() => {
    const chatContainer = document.querySelector(".op_chatbot_body_messages");
    if (!chatContainer) return;

    // Define the scroll handler function
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 30;

      // If user scrolled up manually
      if (scrollTop < lastScrollTop && !isAtBottom) {
        setUserScrolling(true);
      }

      // If user scrolled to bottom
      if (isAtBottom) {
        setUserScrolling(false);
      }

      setLastScrollTop(scrollTop);
    };

    // Add scroll event listener
    chatContainer.addEventListener("scroll", handleScroll);

    // Cleanup function to remove the event listener
    return () => {
      chatContainer.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollTop]); // Only depend on lastScrollTop

  // Initialize message states when messages change
  useEffect(() => {
    setMessageStates((prevStates) => {
      const newStates = { ...prevStates };
      let hasChanges = false;

      messages.forEach((message, index) => {
        if (!newStates[index]) {
          // NEW: Check if message has a rating and initialize accordingly
          if (message.type === "agent" && message.rating !== undefined) {
            newStates[index] = {
              liked: message.rating === 1,
              disliked: message.rating === -1,
              copied: false,
            };
          } else {
            newStates[index] = {
              liked: false,
              disliked: false,
              copied: false,
            };
          }
          hasChanges = true;
        }
      });

      return hasChanges ? newStates : prevStates;
    });
  }, [messages, setMessageStates]);

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  // Send message to parent window when chatbot opens/closes to update z-index
  // Also scroll to bottom when chat is opened
  useEffect(() => {
    // Only send message if we're in an iframe (widget mode)
    if (window.parent !== window) {
      window.parent.postMessage({ type: "CHATBOT_STATE", isOpen: isOpen }, "*");
    }

    // When chat is opened, scroll to bottom
    if (isOpen && endRef.current) {
      // Use RAF for smoother scrolling and to ensure DOM is ready
      requestAnimationFrame(() => {
        // First try scrollIntoView
        endRef.current.scrollIntoView({
          behavior: "auto",
          block: "end",
        });

        // Then force scroll container to bottom
        const chatContainer = document.querySelector(
          ".op_chatbot_body_messages"
        );
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    }
  }, [isOpen, endRef]);

  // Handle email transcript submission
  const handleEmailTranscriptSubmit = async () => {
    try {
      setIsSendingEmail(true);
      const response = await Request.postJson(urls.emailTranscript, {
        session_id: sessionId,
        email: emailInput,
        site_id: siteId,
      });

      if (response?.data?.success === true) {
        CommonNotification.success(response?.data?.message);
        setShowEmailPopup(false);
        setEmailInput("");
        setIsEmailValid(false);
      } else {
        CommonNotification.error("No messages found in this session");
      }
    } catch (error) {
      console.error("Error sending transcript:", error);
      CommonNotification.error(error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handle closing chat widget
  const handleCloseChatWidget = async () => {
    try {
      const storedSessions = JSON.parse(
        localStorage.getItem("chat_session_id") || "{}"
      );

      // Try to find matching key by value
      const keyToDelete = Object.keys(storedSessions).find(
        (k) => storedSessions[k] === sessionId
      );

      // Remove locally if found
      if (keyToDelete) {
        delete storedSessions[keyToDelete];
        localStorage.setItem("chat_session_id", JSON.stringify(storedSessions));
        onCloseMessage && onCloseMessage();
      }

      // Always attempt to delete on server
      if (sessionId && siteId) {
        await Request.deleteJson(urls.deleteChatSession, {
          session_id: sessionId,
          site_id: siteId,
        });
      }
    } catch (error) {
      console.error("Failed to delete chat session:", error);
    } finally {
      setIsOpen(false);
    }
  };

  // Create chat session
  const createChatSession = async (siteId) => {
    try {
      const res = await Request.postJson(urls.createChatSession, {
        site_id: siteId,
      });

      const sessionId = res?.data?.data?.session_id;
      if (sessionId) {
        // Get existing object from localStorage
        const existingSessions =
          JSON.parse(localStorage.getItem("chat_session_id")) || {};

        // Update or add the new site_id: session_id pair
        existingSessions[siteId] = sessionId;

        // Save back to localStorage
        localStorage.setItem(
          "chat_session_id",
          JSON.stringify(existingSessions)
        );

        // Return the session ID so parent component can update its state
        return sessionId;
      }
    } catch (err) {
      console.error("Failed to create chat session:", err);
      return null;
    }
  };

  // Function to handle widget icon click
  const handleWidgetIconClick = async () => {
    // Only check for sessionId if this is an admin_widget (DataSource component)
    // Check if session exists
    if (!sessionId) {
      // If no session ID exists, create a new session
      const newSessionId = await createChatSession(siteId);
      if (newSessionId) {
        setSessionId(newSessionId);
        // Session created successfully, now open the chat
        setIsOpen(true);
      } else {
        // Session creation failed, show error or handle gracefully
        console.error("Failed to create chat session");
        // You might want to show a notification here
        CommonNotification.error(
          "Failed to start chat session. Please try again."
        );
      }
    } else {
      // For other bot types or when session exists, just toggle visibility
      setIsOpen((prev) => !prev);
    }
  };

  // Enhanced renderMessage function with improved feedback UI
  const renderMessage = (message, index) => {
    const messageState = messageStates[index] || {
      liked: false,
      disliked: false,
      copied: false,
    };
    const isAgent = message.type === "agent";

    return (
      <div
        key={`${message.id || index}-${message.timestamp}`}
        data-msg-id={String(message.id ?? index)}
        data-msg-type={isAgent ? "agent" : "user"}
        className={`op_chatbot_body_message inline-flex w-full items-end gap-2 ${
          isAgent ? "justify-start" : "justify-end"
        }`}
      >
        {isAgent && (
          <>
            <div
              className="op_chatbot_body_message_icon mt-[20px] flex-shrink-0 rounded-full p-2"
              style={{ background: agentMessageColor }}
            >
              <HiSparkles
                className="h-4 w-4"
                style={{ color: agentTextColor }}
              />
            </div>
            <div className="max-w-[70%]">
              <div
                className={`op_chatbot_body_message_text chatbot-message -mt-[12px] rounded-t-[10px] rounded-br-[10px] rounded-bl-[3px] px-[15px] py-[10px]`}
                style={{
                  background: agentMessageColor,
                  color: agentTextColor,
                }}
              >
                <div
                  className="op_chatbot_message_html"
                  dangerouslySetInnerHTML={{ __html: message.text }}
                />
                {message.sources && message.sources.length > 0 && (
                  <div className="op_chatbot_sources mt-3 border-t border-white/20 pt-2">
                    <div className="mb-1 text-[16px] opacity-75">Sources:</div>
                    <div className="flex flex-col gap-1">
                      {message.sources.map((source, sourceIndex) => (
                        <a
                          key={sourceIndex}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start break-words text-[14px] opacity-90 transition-all duration-200 hover:underline hover:opacity-100"
                          title={source.title}
                          style={{ color: agentTextColor }}
                        >
                          <span className="mr-1 inline-block text-center">
                            <IoDocumentTextOutline className="mt-[3px] text-[16px]" />
                          </span>
                          <span className="break-words">{source.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="op_chatbot_body_message_rating mt-2 flex items-center gap-[5px]">
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.liked ? "bg-blue-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleMessageRating(index, "like")}
                  title={
                    messageState.liked ? "Remove like" : "Like this message"
                  }
                  disabled={isLoading}
                >
                  <AiOutlineLike
                    className="h-4 w-4"
                    style={{
                      color: messageState.liked ? "#3b82f6" : "#d1d5db",
                      fill: messageState.liked ? "#3b82f6" : "#d1d5db",
                    }}
                  />
                </button>
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.disliked ? "bg-red-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleMessageRating(index, "dislike")}
                  title={
                    messageState.disliked
                      ? "Remove dislike"
                      : "Dislike this message"
                  }
                  disabled={isLoading}
                >
                  <AiOutlineDislike
                    className="h-4 w-4"
                    style={{
                      color: messageState.disliked ? "#ef4444" : "#d1d5db",
                      fill: messageState.disliked ? "#ef4444" : "#d1d5db",
                    }}
                  />
                </button>
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.copied ? "bg-green-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleCopyMessage(index, message.text)}
                  title={messageState.copied ? "Copied!" : "Copy message"}
                >
                  <FiCopy
                    className="h-4 w-4"
                    style={{
                      color: messageState.copied ? "#10b981" : "#d1d5db",
                    }}
                  />
                </button>
              </div>
            </div>
          </>
        )}
        {!isAgent && (
          <>
            <div className="max-w-[70%]">
              <div
                className={`op_chatbot_body_message_text chatbot-message -mt-[12px] rounded-t-[10px] rounded-bl-[10px] rounded-br-[3px] bg-gray-200 px-[15px] py-[5px]`}
              >
                <div
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: message.text }}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.copied ? "bg-green-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleCopyMessage(index, message.text)}
                  title={messageState.copied ? "Copied!" : "Copy message"}
                >
                  <FiCopy
                    className="h-4 w-4"
                    style={{
                      color: messageState.copied ? "#10b981" : "#d1d5db",
                    }}
                  />
                </button>
              </div>
            </div>
            <div className="op_chatbot_body_message_icon flex-shrink-0 rounded-full bg-gray-200 p-2">
              <FiUser className="h-4 w-4 text-gray-600" />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`op_chatbot_widget  opchatbot-wrapper w-[100%] font-sans ${
          isSticky
            ? `sticky top-[100px] bottom-0 ${
                widgetPosition === "bottom-left"
                  ? "left-0 right-auto"
                  : "right-0 left-auto"
              }`
            : "relative h-[100%] w-full"
        } ${bot_type === "readonly" ? "h-[100%] bg-lightPrimary" : ""} ${
          bot_type === "widget" ? "h-[100vh]" : ""
        }`}
      >
        {/* Main Chat Container - Show based on bot_type and sessionId */}
        {(bot_type === "admin_widget" ? sessionId : true) && (
          <div
            className={`op_chatbot_body op-chatbot-scale absolute  shadow ${
              widgetPosition === "bottom-left"
                ? "left-0 origin-bottom-left"
                : "right-0 origin-bottom-right"
            } flex  flex-col overflow-hidden rounded-md bg-white shadow-[1px_1px_10px_0px_rgba(0,0,0,0.1)] ${
              bot_type === "readonly"
                ? widgetPosition === "bottom-left"
                  ? "left-[10px] top-[10px] h-[600px] w-[520px]"
                  : "right-[10px] top-[10px] h-[600px] w-[520px]"
                : bot_type === "admin_widget"
                ? "relative h-[600px] w-[100%]"
                : isSticky
                ? "h-[100vh] w-[100%]"
                : "mt-4  w-[450px]"
            }
           ${isOpen ? " open" : ""}`}
            style={{ borderColor: headerColor }}
            aria-hidden={!isOpen}
          >
            {/* Chat Header */}
            <div
              className="op_chatbot_header flex items-center gap-2 p-4"
              style={{ background: headerColor }}
            >
              <div className="op_chatbot_header_icon">
                <HiSparkles className="h-6 w-6 text-white" />
              </div>
              <div className="op_chatbot_header_text flex-grow">
                <h4 className="font-semibold text-white">
                  Chat with SmartBot
                </h4>
                <p className="text-white">Ask anything you want</p>
              </div>
              <div className="op_chatbot_header_settings cursor-pointer">
                <Dropdown
                  button={
                    <div className="flex cursor-pointer items-center gap-2 rounded-md dark:!border-white/10">
                      <IoIosOptions className="h-5 w-5 text-white" />
                    </div>
                  }
                  children={
                    <ul className="flex w-[120px] flex-col gap-2 rounded-[10px] bg-white shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
                      <li
                        className="linear flex cursor-pointer items-center justify-start rounded-md px-2 py-[8px] text-sm text-navy-700 transition duration-200 hover:bg-gray-200 hover:text-navy-700 dark:text-white dark:hover:bg-white/20 dark:hover:text-white dark:active:bg-white/10"
                        onClick={() => setShowEmailPopup(true)}
                      >
                        Email Transcript
                      </li>
                    </ul>
                  }
                  classNames={"py-2 top-4 right-0 w-max"}
                  animation="origin-top-right transition-all duration-300 ease-in-out"
                />
              </div>
              <div
                className="op_chatbot_header_close cursor-pointer"
                onClick={handleCloseChatWidget}
              >
                <BsX className="h-6 w-6 text-white" />
              </div>
            </div>

            {showEmailPopup && (
              <div className="absolute top-28 left-1/2 z-40 w-80 -translate-x-1/2 rounded-lg border bg-white p-4 shadow-lg">
                <h4 className="mb-2 text-lg font-semibold">Email Transcript</h4>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    const email = e.target.value;
                    setEmailInput(email);
                    setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
                  }}
                  placeholder="Enter your email"
                  className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-brand-500 focus:outline-none"
                />
                <div className="flex justify-end gap-2">
                  <button
                    className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300"
                    onClick={() => setShowEmailPopup(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleEmailTranscriptSubmit}
                    disabled={!isEmailValid || isSendingEmail}
                  >
                    {isSendingEmail ? (
                      <>
                        Sending... <Spin size="small" />
                      </>
                    ) : (
                      "Send"
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Messages Container */}
            <div className="op_chatbot_body_messages mt-2 grid flex-grow content-start gap-1 overflow-y-auto scroll-smooth p-4">
              {messages.length === 0 && !typingMessage ? (
                // Default welcome message when no messages exist
                <div className="op_chatbot_body_message op_chatbot_body_message_agent inline-flex items-end justify-items-end gap-2 justify-self-start">
                  <div
                    className="op_chatbot_body_message_icon rounded-full p-2 "
                    style={{ background: agentMessageColor }}
                  >
                    <HiSparkles
                      className="h-4 w-4"
                      style={{ color: agentTextColor }}
                    />
                  </div>
                  <div className="max-w-[80%]">
                    <div
                      className="op_chatbot_body_message_text chatbot-message -mt-[12px] rounded-t-[10px] rounded-br-[10px] rounded-bl-[3px] px-[15px] py-[10px]"
                      style={{
                        background: agentMessageColor,
                        color: agentTextColor,
                      }}
                    >
                      <p>
                        Hi, I am SmartBot, your virtual assistant. How can I
                        help you today?
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Render dynamic messages */}
                  {messages.map((message, index) =>
                    renderMessage(message, index)
                  )}

                  {/* Render typing message */}
                  {typingMessage && (
                    <div
                      className="op_chatbot_body_message op_chatbot_body_message_agent inline-flex items-end justify-items-end gap-2 justify-self-start"
                      style={{ willChange: "contents" }} // Optimize for animations
                    >
                      <div
                        className="op_chatbot_body_message_icon rounded-full p-2"
                        style={{ background: agentMessageColor }}
                      >
                        <HiSparkles
                          className="h-4 w-4"
                          style={{ color: agentTextColor }}
                        />
                      </div>
                      <div className="max-w-[80%]">
                        <div
                          className="op_chatbot_body_message_text chatbot-message -mt-[12px] rounded-md rounded-t-[10px] rounded-bl-[10px] rounded-br-[3px] px-[15px] py-[5px]"
                          style={{
                            background: agentMessageColor,
                            color: agentTextColor,
                          }}
                        >
                          <div className="whitespace-pre-wrap">
                            <span
                              style={{
                                willChange: "contents",
                                display: "inline-block",
                                minHeight: "1.2em",
                                transform: "translateZ(0)", // Force GPU acceleration
                              }}
                            >
                              {typingMessage.text}
                            </span>
                          </div>

                          {/* Sources will only be shown after typing is complete */}
                          {false &&
                            typingMessage.sources &&
                            typingMessage.sources.length > 0 && (
                              <div className="op_chatbot_sources mt-3 border-t border-white/20 pt-2">
                                <div className="mb-1 text-[16px] opacity-75">
                                  Sources:
                                </div>
                                <div className="flex flex-col gap-1">
                                  {typingMessage.sources.map(
                                    (source, sourceIndex) => (
                                      <a
                                        key={sourceIndex}
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block break-words text-xs opacity-90 transition-all duration-200 hover:underline hover:opacity-100"
                                        title={source.title}
                                        style={{ color: agentTextColor }}
                                      >
                                        <span className="mr-1 inline-block w-3 text-center">
                                          📄
                                        </span>
                                        <span className="break-words">
                                          {source.title}
                                        </span>
                                      </a>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Loading indicator — stepped status loader */}
              {isLoading && !typingMessage && (
                <div className="op_chatbot_body_message op_chatbot_body_message_agent inline-flex items-end justify-items-end gap-2 justify-self-start">
                  <div
                    className="op_chatbot_body_message_icon rounded-full p-2"
                    style={{ background: agentMessageColor }}
                  >
                    <HiSparkles
                      className="h-4 w-4"
                      style={{ color: agentTextColor }}
                    />
                  </div>
                  <div className="max-w-[80%]">
                    <div
                      className="op_chatbot_body_message_text chatbot-message -mt-[12px] rounded-t-[10px] rounded-br-[10px] rounded-bl-[3px] px-[15px] py-[10px]"
                      style={{
                        background: agentMessageColor,
                        color: agentTextColor,
                      }}
                    >
                      <ChatbotStepLoader textColor={agentTextColor} />
                    </div>
                  </div>
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={endRef} />
            </div>

            {/* Input Form */}
            <form
              className="w-full p-4 pb-0"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <div className="relative flex w-full items-center">
                <input
                  type="text"
                  placeholder="Type your message..."
                  className="flex-grow rounded-l-md border border-gray-200 px-3 py-2 pr-10 focus:outline-none"
                  value={input}
                  readOnly={bot_type === "readonly"}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={isLoading}
                  onKeyDown={handleKeyDown}
                  maxLength={200}
                />
                <div className="absolute inset-y-0 right-0 flex h-[100%] items-center">
                  <button
                    type="submit"
                    className="flex h-[100%] w-9 items-center justify-center rounded-r-md text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Send"
                    disabled={!input.trim() || isLoading}
                    style={{ background: agentMessageColor }}
                  >
                    {isLoading ? (
                      <svg
                        className="h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <AiOutlineSend className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className=" text-right mt-2 text-[12px] text-[#757575]">
                {input.length}/200 characters
              </div>
            </form>
            <div className="chatbot-footer-text">
              <p>
                Chat history is stored in accordance with our
                <a
                  href="https://www.smartroots.us/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {" "}
                  privacy policy{" "}
                </a>
                . By Smart Roots
              </p>
            </div>
          </div>
        )}
      </div>
      <div
        className={`op_chatbot_icon absolute ${
          widgetPosition === "bottom-left" ? "left-0" : "right-0"
        } ${
          bot_type === "widget"
            ? isOpen
              ? "bottom-[0px] right-[0]"
              : "bottom-[0px] right-[0]"
            : isSticky
            ? "bottom-[0px]"
            : "bottom-[280px]"
        } ${
          bot_type === "readonly" 
            ? widgetPosition === "bottom-left"
              ? "left-[30px]" 
              : "right-[30px]"
            : "right-[0]"
        } flex h-[50px] w-[50px] cursor-pointer items-center justify-center rounded-full transition-all duration-300 ease-in-out `}
        style={{ background: widgetIconColor }}
        onClick={handleWidgetIconClick}
      >
        {widgetIcon ? (
          <img
            src={widgetIcon}
            alt="Bot Avatar"
            className="h-10 w-10 rounded-full object-cover"
            style={{ background: widgetIconColor }}
          />
        ) : (
          <BsChatDots className="h-6 w-6 text-white" />
        )}
      </div>
    </>
  );
};

// ... your existing PropTypes
ChatbotWidget.propTypes = {
  widgetPosition: PropTypes.oneOf(["bottom-left", "bottom-right"]),
  headerColor: PropTypes.string,
  agentMessageColor: PropTypes.string,
  agentTextColor: PropTypes.string,
  widgetIconColor: PropTypes.string,
  widgetIcon: PropTypes.string,
  input: PropTypes.string,
  setInput: PropTypes.func,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  messagesEndRef: PropTypes.object,
  bot_type: PropTypes.oneOf(["widget", "readonly", "admin_widget"]),
  liked: PropTypes.bool,
  setLiked: PropTypes.func,
  disliked: PropTypes.bool,
  setDisliked: PropTypes.func,
  copiedMsg: PropTypes.bool,
  setCopiedMsg: PropTypes.func,
  // New PropTypes for dynamic functionality
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["user", "agent"]).isRequired,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sources: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  onSendMessage: PropTypes.func,
  onStartTyping: PropTypes.func,
  onFinishTyping: PropTypes.func,
  sessionId: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSticky: PropTypes.bool,
};

export default ChatbotWidget;
